import React, { useState, useEffect } from 'react';
import { drinksAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const DrinksTab = () => {
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDrink, setShowAddDrink] = useState(false);
  const [editingDrink, setEditingDrink] = useState(null);
  const [drinkForm, setDrinkForm] = useState({
    name: '',
    value: '',
    displayOrder: 999
  });

  useEffect(() => {
    loadDrinks();
  }, []);

  const loadDrinks = async () => {
    try {
      setLoading(true);
      const response = await drinksAPI.getAllDrinks();
      if (response.success) {
        setDrinks(response.drinks || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Getränke:', error);
      toast.error('Fehler beim Laden der Getränke');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setDrinkForm({
      ...drinkForm,
      [name]: value
    });
  };

  const handleAddDrink = async (e) => {
    e.preventDefault();

    try {
      const response = await drinksAPI.createDrink(drinkForm);
      if (response.success) {
        toast.success('Getränk wurde erfolgreich hinzugefügt!');
        setDrinkForm({ name: '', value: '', displayOrder: 999 });
        setShowAddDrink(false);
        await loadDrinks();
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Getränks:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Hinzufügen des Getränks');
    }
  };

  const handleUpdateDrink = async (e) => {
    e.preventDefault();

    try {
      const response = await drinksAPI.updateDrink(editingDrink.id, drinkForm);
      if (response.success) {
        toast.success('Getränk wurde erfolgreich aktualisiert!');
        setEditingDrink(null);
        setDrinkForm({ name: '', value: '', displayOrder: 999 });
        await loadDrinks();
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Getränks:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Aktualisieren des Getränks');
    }
  };

  const handleDeleteDrink = async (drinkId) => {
    if (!window.confirm('Möchten Sie dieses Getränk wirklich löschen?')) {
      return;
    }

    try {
      const response = await drinksAPI.deleteDrink(drinkId);
      if (response.success) {
        toast.success('Getränk wurde gelöscht!');
        await loadDrinks();
      }
    } catch (error) {
      console.error('Fehler beim Löschen des Getränks:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Löschen des Getränks');
    }
  };

  const startEdit = (drink) => {
    setEditingDrink(drink);
    setDrinkForm({
      name: drink.name,
      value: drink.value,
      displayOrder: drink.display_order || 999
    });
    setShowAddDrink(false);
  };

  const cancelEdit = () => {
    setEditingDrink(null);
    setDrinkForm({ name: '', value: '', displayOrder: 999 });
  };

  if (loading) {
    return <div className="loading">Getränke werden geladen...</div>;
  }

  return (
    <div className="drinks-tab">
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
        <h3>Verfügbare Getränke ({drinks.length})</h3>
        <button
          onClick={() => {
            setShowAddDrink(true);
            setEditingDrink(null);
            setDrinkForm({ name: '', value: '', displayOrder: 999 });
          }}
          className="btn btn-primary"
        >
          + Neues Getränk hinzufügen
        </button>
      </div>

      {/* Formular für Hinzufügen/Bearbeiten */}
      {(showAddDrink || editingDrink) && (
        <div className="modal-overlay" onClick={() => {
          setShowAddDrink(false);
          cancelEdit();
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDrink ? 'Getränk bearbeiten' : 'Neues Getränk hinzufügen'}</h2>
              <button className="close-btn" onClick={() => {
                setShowAddDrink(false);
                cancelEdit();
              }}>×</button>
            </div>
            <form onSubmit={editingDrink ? handleUpdateDrink : handleAddDrink} className="game-form">
              <div className="form-group">
                <label htmlFor="name">Name (angezeigt) *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={drinkForm.name}
                  onChange={handleFormChange}
                  required
                  placeholder="z.B. Bier, Äppler, Cola"
                />
              </div>

              <div className="form-group">
                <label htmlFor="value">Wert (technisch) *</label>
                <input
                  type="text"
                  id="value"
                  name="value"
                  value={drinkForm.value}
                  onChange={handleFormChange}
                  required
                  placeholder="z.B. bier, aeppler, cola (kleinbuchstaben, keine Leerzeichen)"
                />
                <small style={{color: '#666', fontSize: '12px'}}>
                  Nur Kleinbuchstaben, Zahlen und Bindestriche erlaubt
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="displayOrder">Anzeigereihenfolge</label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={drinkForm.displayOrder}
                  onChange={handleFormChange}
                  min="0"
                  max="9999"
                />
                <small style={{color: '#666', fontSize: '12px'}}>
                  Niedrigere Zahlen erscheinen zuerst (Standard: 999)
                </small>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingDrink ? 'Speichern' : 'Hinzufügen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDrink(false);
                    cancelEdit();
                  }}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Getränke-Liste */}
      {drinks.length === 0 ? (
        <div className="empty-state">
          <p>Noch keine Getränke vorhanden.</p>
          <p>Fügen Sie jetzt das erste Getränk hinzu!</p>
        </div>
      ) : (
        <>
          {/* Desktop Tabelle */}
          <div className="games-table-wrapper drinks-desktop-table">
            <table className="games-table">
              <thead>
                <tr>
                  <th style={{width: '100px', textAlign: 'center'}}>Reihenfolge</th>
                  <th>Name</th>
                  <th style={{width: '150px', textAlign: 'center'}}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {drinks.map((drink) => (
                  <tr key={drink.id}>
                    <td style={{textAlign: 'center'}}>{drink.display_order}</td>
                    <td><strong>{drink.name}</strong></td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => startEdit(drink)}
                          className="btn btn-sm btn-primary"
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteDrink(drink.id)}
                          className="btn btn-sm btn-danger"
                          title="Löschen"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card Layout */}
          <div className="drinks-mobile-grid">
            {drinks.map((drink) => (
              <div key={drink.id} className="drink-mobile-card">
                <div className="drink-mobile-header">
                  <div>
                    <div className="drink-mobile-name">{drink.name}</div>
                    <div style={{fontSize: '0.8rem', color: '#666'}}>Reihenfolge: {drink.display_order}</div>
                  </div>
                </div>
                <div className="drink-mobile-actions">
                  <button
                    onClick={() => startEdit(drink)}
                    className="btn btn-sm btn-primary"
                    title="Bearbeiten"
                  >
                    ✏️ Bearbeiten
                  </button>
                  <button
                    onClick={() => handleDeleteDrink(drink.id)}
                    className="btn btn-sm btn-danger"
                    title="Löschen"
                  >
                    🗑️ Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DrinksTab;
